import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import SEOHelmet from '../components/SEOHelmet';
import '../styles/BarcodeDetailsPage.css';

const BarcodeDetailsPage = () => {
  const { encodedPayload } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scanData, setScanData] = useState(null);

  useEffect(() => {
    const queryPayload = new URLSearchParams(location.search).get('data');
    const payload = encodedPayload || queryPayload;

    const fetchBarcodeDetails = async () => {
      setLoading(true);
      setError('');
      try {
        const token = sessionStorage.getItem('token');
        const response = await axios.get(
          `${API_BASE_URL}/api/asset-users/barcode/${payload}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          }
        );

        if (response.data?.success) {
          setScanData(response.data.data);
        } else {
          setError('Unable to load barcode details');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load barcode details');
      } finally {
        setLoading(false);
      }
    };

    if (payload) {
      fetchBarcodeDetails();
    } else {
      setError('Invalid barcode link');
      setLoading(false);
    }
  }, [encodedPayload, location.search]);

  const specsEntries = useMemo(() => {
    const specs = scanData?.asset?.specifications;
    if (!specs) return [];
    if (Array.isArray(specs)) return specs;
    return Object.entries(specs);
  }, [scanData]);

  const formatDate = (value) => {
    if (!value) return 'N/A';
    return new Date(value).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    return duration
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  const calculateReturnDate = (assignedDate, duration) => {
    if (!assignedDate || !duration) return 'N/A';
    const startDate = new Date(assignedDate);
    const durationValue = parseInt(duration.split('_')[0]);
    const durationUnit = duration.split('_')[1] || 'Weeks';

    let returnDate = new Date(startDate);
    if (durationUnit.toLowerCase().includes('week')) {
      returnDate.setDate(returnDate.getDate() + durationValue * 7);
    } else if (durationUnit.toLowerCase().includes('month')) {
      returnDate.setMonth(returnDate.getMonth() + durationValue);
    } else if (durationUnit.toLowerCase().includes('day')) {
      returnDate.setDate(returnDate.getDate() + durationValue);
    }

    return returnDate.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="barcode-details-page"><div className="state-box">Loading barcode details...</div></div>;
  }

  if (error) {
    return <div className="barcode-details-page"><div className="state-box error">{error}</div></div>;
  }

  if (!scanData) {
    return <div className="barcode-details-page"><div className="state-box">No data found.</div></div>;
  }

  const { asset, unit, activeAllocation, assignmentHistory } = scanData;

  return (
    <div className="barcode-details-page">
      <SEOHelmet page="barcodeDetails" canonicalUrl="https://www.assetflows.in/barcode" noIndex />
      <div className="barcode-details-wrap">
        <h1>Scanned Asset Details</h1>

        <section className="details-card">
          <h2>Asset Details</h2>
          <div className="details-grid">
            <div><strong>Asset Name:</strong> {asset.assetName}</div>
            <div><strong>Unit ID:</strong> {unit.unitId}</div>
            <div><strong>Category:</strong> {asset.category || 'N/A'}</div>
            <div><strong>Type:</strong> {asset.assetType || 'N/A'}</div>
            <div><strong>Brand / Model:</strong> {[asset.brand, asset.model].filter(Boolean).join(' / ') || 'N/A'}</div>
            <div><strong>Serial Number:</strong> {asset.serialNumber || 'N/A'}</div>
            <div><strong>Department:</strong> {asset.department || 'N/A'}</div>
            <div><strong>Current Location:</strong> {asset.currentLocation || 'N/A'}</div>
            <div><strong>Condition:</strong> {asset.condition || 'N/A'}</div>
            <div><strong>Purchase Date:</strong> {formatDate(asset.purchaseDate)}</div>
            <div><strong>Warranty Expiry:</strong> {formatDate(asset.warrantyExpiryDate)}</div>
            <div><strong>Allocation Status:</strong> <span className={activeAllocation ? 'allocated-status' : 'unallocated-status'}>{activeAllocation ? 'Allocated' : 'Unallocated'}</span></div>
          </div>

          {specsEntries.length > 0 && (
            <div className="spec-section">
              <h3>Specifications</h3>
              <div className="spec-grid">
                {specsEntries.map(([k, v]) => (
                  <div key={k} className="spec-row">
                    <span>{k}</span>
                    <strong>{v || 'N/A'}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="details-card">
          <h2>Active Allocation</h2>
          {activeAllocation ? (
            <div className="details-grid">
              <div><strong>User:</strong> {activeAllocation.user?.name || 'N/A'}</div>
              <div><strong>User Department:</strong> {activeAllocation.user?.department || 'N/A'}</div>
              <div><strong>Desk/Location:</strong> {activeAllocation.deskLocation || 'N/A'}</div>
              <div><strong>Status:</strong> {activeAllocation.status}</div>
              <div><strong>Assigned On:</strong> {formatDate(activeAllocation.assignedDate)}</div>
              <div><strong>Duration:</strong> {formatDuration(activeAllocation.duration)}</div>
              <div><strong>Return Date:</strong> {calculateReturnDate(activeAllocation.assignedDate, activeAllocation.duration)}</div>
              <div><strong>Request Type:</strong> {(activeAllocation.requestType || 'N/A').replace('_', ' ')}</div>
            </div>
          ) : (
            <p className="muted">No active allocation for this unit.</p>
          )}
        </section>

        <section className="details-card">
          <h2>Usage History</h2>
          {assignmentHistory?.length ? (
            <div className="history-table-wrap">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Department</th>
                    <th>Desk/Location</th>
                    <th>Assigned On</th>
                    <th>Return Date</th>
                    <th>Duration</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {assignmentHistory.map((row, idx) => (
                    <tr key={row.assignmentId || idx}>
                      <td>{idx + 1}</td>
                      <td>{row.user?.name || 'N/A'}</td>
                      <td>{row.user?.department || 'N/A'}</td>
                      <td>{row.deskLocation || 'N/A'}</td>
                      <td>{formatDate(row.assignedDate)}</td>
                      <td>{calculateReturnDate(row.assignedDate, row.duration)}</td>
                      <td>{formatDuration(row.duration)}</td>
                      <td>{row.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="muted">No assignment history found for this unit.</p>
          )}
        </section>
      </div>
    </div>
  );
};

export default BarcodeDetailsPage;
